package com.neurobs.ccabserver.administrator.view

import com.neurobs.ccab.{TestPerformance, TestRunDetail, TestStatus}
import com.neurobs.ccabserver.administrator.controller.{ExaminerCommentController, TestRunDetailInstance}
import com.neurobs.js.dom.{CollapsibleBox, ElementFactory, Grid, GridCell, HideableRowGrid, StopWatchElement}
import com.neurobs.js.dom.Grid._
import com.neurobs.js.view.{Container, Gui}
import com.neurobs.js.dom.DOMViewElement._
import org.scalajs.dom.raw.HTMLElement
import scalatags.JsDom.all._
import com.neurobs.js.dom.ElementFactory._

import scala.collection.mutable.ArrayBuffer

class TestDetailView {
	private var currentComments: Option[ExaminerCommentController] = None

	private val grid: HideableRowGrid = new HideableRowGrid(fullWidth = true, emGapSize = ElementFactory.defaultGridSpacingEm)

	private val contentDiv = div().render
	grid.addRow(contentDiv)

	private val commentsDiv = div().render
	grid.addRow(commentsDiv)

	private val collapse = CollapsibleBox(title = "", content = grid.container)

	val container = collapse.container

	def update(detail: TestRunDetailInstance, title: String, showComments: Boolean, expand: Option[Boolean]): Unit = {
		updateContent(detail.detail)
		collapse.setTitle(title)
		grid.setRowVisible(commentsDiv, showComments)
		expand.foreach(collapse.setVisible(_))
		//println(s"${commentsDiv.childElementCount} children for $title")
		if ((Some(detail.commentController) != currentComments) || (commentsDiv.childElementCount == 0)) {
			commentsDiv.clear()
			commentsDiv.appendChild(toElement(detail.commentController.container))
		}
		currentComments = Some(detail.commentController)
	}

	private def updateContent(detail: TestRunDetail): Unit = {
		contentDiv.clear()
		val grid: Grid = new Grid(fullWidth = true, emGapSize = ElementFactory.defaultGridSpacingEm)

		if (detail.run.error.isDefined || detail.stop.isDefined) {
			val message = detail.run.error match {
				case Some(e) => e
				case None => "The test was stopped: " + detail.stop.get.reason
			}
			grid.addRow(div(`class` := TestDetailView.errorClass, message).render)
		}

		val duration = detail.end_time match {
			case None => StopWatchElement.make(detail.start_time)
			case Some(et) => span(StopWatchElement.formatDifference(detail.start_time, et)).render
		}
		val topGrid = new Grid(fullWidth = true, emGapSize = 0.5, defaultAlign = Grid.alignCenter)
		val columns = ArrayBuffer[GridCell]()
		val infoRows = ArrayBuffer[(HTMLElement, HTMLElement)](
			("Start Time:", Gui.toLocalTime(detail.start_time) ),
			("Duration:", duration),
			("Status:", TestDetailView.makeStatus(detail))
		)
		detail.start.foreach { s =>
			infoRows.append(("Start Position:", span(s).render))
		}
		columns.append(makeParameterTable(infoRows.toArray))

		if (detail.perf.nonEmpty) {
			val perf = div(style := "display: inline-block; text-align: center").render
			//val grid = new Grid(fullWidth = false, emGapSize = 0.5, defaultAlign = Grid.alignCenter)
			for (p <- detail.perf) {
				perf.appendChild(TestDetailView.makeSection(p))
				//grid.addRow(makeSection(p))
			}
			//columns.append(grid.container)
			columns.append(perf)
		}
		topGrid.addRowCells(columns)
		grid.addRow(topGrid.container)

		contentDiv.appendChild(grid.container)
	}
}

object TestDetailView {
	private val errorClass = "test-state-error"
	private val statusCompletedClass = "test-state-status-completed"
	private val statusInProgressClass = "test-state-status-in-progress"
	private val statusPendingClass = "test-state-status-pending"
	private val statusErrorClass = "test-state-status-error"

	private def makeStatus(detail: TestRunDetail): HTMLElement = {
		val (caption, styleClass) = detail.status match {
			case TestStatus.Paused => ("Paused", statusPendingClass)
			case TestStatus.Running => ("Running", statusInProgressClass)
			case TestStatus.Finished =>
				if (detail.run.error.isDefined) ("Error", statusErrorClass)
				else if (detail.stop.isDefined) ("Failed", statusErrorClass)
				else ("Finished", statusCompletedClass)
		}
		div(style := styleClass, caption).render
	}

	private def makeSection(data: TestPerformance): HTMLElement = {
		val grid = new Grid(fullWidth = true, emGapSize = 0.5, defaultAlign = Grid.alignCenter)
		grid.addRow(b(data.section).render)
		val valueRows = ArrayBuffer[(HTMLElement, HTMLElement)]()
		data.values.foreach { values =>
			values.foreach { v => valueRows.append((stringToElement(v.name + ":"), stringToElement(v.value))) }
		}
		data.criteria.foreach { cs =>
			for (c <- cs) {
				valueRows.append((stringToElement(c.name + ":"), span(c.value, s" (${c.required} required) ", makeCriterionStatus(c.status).render).render))
			}
		}
		if (valueRows.nonEmpty) {
			grid.addRow(makeParameterTable(valueRows.toArray, useBorders = true))
		}
		data.graphs.foreach( { gs =>
			for (g <- gs) {
				grid.addRow(ChartView.draw(g, divStyle = "width: 400px;"))
			}
		})
		grid.container
	}

	private def makeCriterionStatus(status: String): HTMLElement = {
		val (caption, bcolor) = status match {
			case "In Progress" => ("In Progress", "SteelBlue")
			case "Passed" => ("Passed", "green")
			case "Failed" => ("Failed", "red")
			case "Repeat" => ("Repeat", "orange")
			case "Warning" => ("Warning", "orange")
			case _ => (status, "black")
		}

		span(style := s"color: #FFFFFF; background-color: $bcolor;", caption).render
	}

	def apply(detail: TestRunDetailInstance, title: String, showComments: Boolean, expand: Boolean): TestDetailView = {
		val view = new TestDetailView
		view.update(detail = detail, title = title, showComments = showComments, expand = Some(expand))
		view
	}
}
